import { hasFullAccess, isLiveDemo, isScouting } from '../../utils/capabilities';
const CATEGORY = {
    hd_demo: { key: 'hd_demo', label: 'HD Demo', color: '#06b6d4' }, // calm cyan (active demo)
    low_res: { key: 'low_res', label: 'Low-Res', color: '#9a3412' }, // rust
    completed: { key: 'completed', label: 'Completed', color: '#10b981' },
    // Full access, preview fidelity: the full-res outputs are absent, so the
    // project works but is not design-ready. Informative violet / slate, never a
    // warning colour: nothing is wrong, it just has not been built yet.
    preview: { key: 'preview', label: 'Preview', color: '#8b5cf6' },
    // The rendered sets are gone and the server is regenerating the preview, so
    // the project reports zero artifacts on purpose. Indigo sits between preview's
    // violet and unbuilt's slate: the same informational family, never a warning —
    // nothing failed, the layers are on their way back. Label kept to the width of
    // the other badges; the list sub-line and the panel carry the full sentence.
    restoring_preview: { key: 'restoring_preview', label: 'Restoring…', color: '#6366f1' },
    unbuilt: { key: 'unbuilt', label: 'Not Built', color: '#64748b' },
    partial: { key: 'partial', label: 'Partial', color: '#f59e0b' },
    // Wire value stays 'unpaid' (the server also emits a distinct 'locked' for
    // lapsed demos, shown as Low-Res); only the user-facing label reads Locked.
    unpaid: { key: 'unpaid', label: 'Locked', color: '#dc2626' },
    processing: { key: 'processing', label: 'Processing', color: '#3b82f6' },
    // The elevation-data provider stayed unreachable past the retry budget, so we
    // could not find out whether data exists here. Amber like the other unsettled
    // states, never the red of a failure: nothing broke and nothing is terminal.
    still_looking: { key: 'still_looking', label: 'Still looking for data', color: '#f59e0b' },
    pending: { key: 'pending', label: 'Pending', color: '#f59e0b' },
    failed: { key: 'failed', label: 'Failed', color: '#dc2626' },
};
const isDemoExpired = (project) => project.demoExpiresAt != null && project.demoExpiresAt - Date.now() / 1000 <= 0;
export function categorizeProject(project) {
    const { accessLevel, displayStatus } = project;
    // Entitlement axis first (the gate's own verdict). A live demo whose window
    // has elapsed since fetch is low-res in reality, matching the badge countdown.
    if (isLiveDemo(accessLevel) && !isDemoExpired(project))
        return CATEGORY.hd_demo;
    if (isScouting(accessLevel) || (isLiveDemo(accessLevel) && isDemoExpired(project))) {
        return CATEGORY.low_res;
    }
    // Full access: done or still building. A partial pipeline failure keeps full
    // access, so it must not read as a clean "Completed" — the amber Partial badge
    // is driven by hasFailedLayers (the completeness fact), not a fused status.
    if (hasFullAccess(accessLevel)) {
        if (displayStatus === 'processing')
            return CATEGORY.processing;
        // Still in progress, just stalled on an unreachable data provider. Named
        // ahead of every completeness read for the same reason as processing: there
        // is nothing built here yet to be complete or partial.
        if (displayStatus === 'still_looking')
            return CATEGORY.still_looking;
        // The preview set is being regenerated, so there is nothing to serve yet.
        // Ahead of every completeness read: zero artifacts here is the expected
        // reading of a working project, not an empty or partial one.
        if (displayStatus === 'restoring_preview')
            return CATEGORY.restoring_preview;
        // A terminally failed build keeps full access, so the failure must be named
        // here too, ahead of hasFailedLayers (which describes a partial success).
        if (displayStatus === 'failed')
            return CATEGORY.failed;
        // Full access but nothing full-resolution to show: 'preview' means the
        // full-res outputs were removed and the preview set is being served,
        // 'unbuilt' means the project has never been processed. Both are ahead of
        // the partial/completed reads, which would claim a fidelity that isn't there.
        if (displayStatus === 'preview')
            return CATEGORY.preview;
        if (displayStatus === 'unbuilt')
            return CATEGORY.unbuilt;
        if (project.hasFailedLayers)
            return CATEGORY.partial;
        return CATEGORY.completed;
    }
    // No access (accessLevel 'none' or absent): displayStatus names which
    // build/billing state this is — presentation only, never a gate.
    switch (displayStatus) {
        case 'unpaid':
            return CATEGORY.unpaid;
        case 'processing':
            return CATEGORY.processing;
        case 'still_looking':
            return CATEGORY.still_looking;
        // A lapsed demo whose preview set has not landed yet: it reads
        // 'restoring_preview' until the build finishes, then 'locked' as before.
        case 'restoring_preview':
            return CATEGORY.restoring_preview;
        case 'pending':
            return CATEGORY.pending;
        case 'failed':
            return CATEGORY.failed;
        default:
            // Unknown / transitional states (e.g. 'created') fall back to the raw
            // status as their own label so nothing silently disappears from a filter.
            return { key: displayStatus, label: displayStatus, color: 'var(--pewter)' };
    }
}
// The distinct categories present across a set of projects, in a stable display
// order, so the filter only offers types the user actually has.
const DISPLAY_ORDER = [
    'completed',
    'preview',
    'restoring_preview',
    'unbuilt',
    'hd_demo',
    'low_res',
    'partial',
    'unpaid',
    'processing',
    'still_looking',
    'pending',
    'failed',
];
export function presentCategories(projects) {
    const byKey = new Map();
    for (const p of projects) {
        const c = categorizeProject(p);
        if (!byKey.has(c.key))
            byKey.set(c.key, c);
    }
    const ordered = [];
    for (const key of DISPLAY_ORDER) {
        const c = byKey.get(key);
        if (c) {
            ordered.push(c);
            byKey.delete(key);
        }
    }
    // Any fallback/unknown categories trail the known ones.
    for (const c of byKey.values())
        ordered.push(c);
    return ordered;
}
